// Validation rule schema. Template rule configs are an
// overlay keyed by worksheet + field name; buildTemplateDefinition zips them with the
// generated field definitions (staticDataFields.ts / balanceImportFields.ts), which stay the
// source of truth for field names and requirement levels.

import type { TemplateType } from "services/DataImportService";
import { TemplateFieldDef } from "../steps/Mapping/staticDataFields";

// String-union mirror of the generated TemplateType enum. templateConfig and the validation
// engine run inside the worker: importing services/CPLAPI here would drag the whole generated
// client (and break plain-Jest suites) into that compilation unit. Enum values are the same
// strings; callers convert at the boundary.
export type TemplateKind = "StaticData" | "BalanceImport";

// Same values as the generated TemplateType, so worker code never imports the generated API client
export function toTemplateKind(templateType: TemplateType): TemplateKind {
  const templateKind: `${TemplateType}` = templateType;
  return templateKind;
}

// Fixed lists authored as FE constants — including the
// region/context-specific subsets the Types API does not expose.
export type StaticListName =
  | "Yes_No"
  | "PLPaymentType"
  | "VATCode"
  | "Period"
  | "Months"
  | "ClientAccountType"
  | "PropertyOnlyPropertyServiceChargeType";

// DB-backed lists from GET /api/Types/Get* (DataImportReferenceData).
export type ReferenceListName =
  | "AccountTypes"
  | "PropertyTypes"
  | "AccountingBasisTypes"
  | "PropertyServiceChargeTypes"
  | "AuthorityToActTypes"
  | "ListedBuildingTypes"
  | "FreeholderCommissionRateTypes"
  | "ClientPaymentTypes"
  | "FundScheduleChargeTypes"
  | "Countries";

export type ListRef =
  // FE-constant list (staticLists.ts).
  | { source: "static"; list: StaticListName }
  // DB-backed list posted into the engine from the dataImportTypes slice.
  | { source: "referenceData"; list: ReferenceListName }
  // Dynamic list sourced from the customer's own data — an FK index built at run time
  // from another worksheet of the same submission (e.g. Nominals, ChargeDates). An array of
  // fields accepts a value found in ANY of those columns (union index).
  | { source: "sheet"; worksheet: string; field: string | string[] }
  // RESERVED (cross-template): key set from a paired submission, e.g. Balance
  // Import validating against the stored Static Data keys. Not evaluated yet.
  | { source: "external"; keySet: string };

export type ValidationRule =
  | { type: "required" }
  | { type: "textMaxLength"; max: number }
  | { type: "textLengthRange"; min: number; max: number }
  | { type: "wholeNumberRange"; min: number; max: number }
  | { type: "decimalRange"; min: number; max: number }
  // Accepts a decimal or a written fraction, e.g. 0.25 or 1/4.
  | { type: "fractionRange"; min: number; max: number }
  | { type: "date" }
  | { type: "email" }
  | { type: "phone" }
  | { type: "sortCode" }
  | { type: "allowedValues"; list: ListRef }
  // FK lookup — cross-sheet referential integrity is a lookup with a sheet ListRef.
  | { type: "lookup"; list: ListRef }
  // trigger is the plain-language explanation shown to the user, e.g. "Asbestos Present is Y".
  | {
      type: "conditionalRequired";
      when: { field: string; equals?: string[]; notBlank?: boolean };
      trigger: string;
    }
  // abs compares magnitudes: |value| op |other| (e.g. |Outstanding| ≤ |Total|).
  | { type: "crossFieldCompare"; op: "gte" | "lte" | "eq"; otherField: string; as: "date" | "number"; abs?: boolean }
  // sum(terms) must equal equalsField, e.g. Goods + VAT = Total.
  | { type: "crossFieldArithmetic"; terms: string[]; equalsField: string; tolerance?: number }
  // Computed by the tool on submission — the engine skips it; kept for config fidelity.
  | { type: "autoDerived" };

// Sheet-scoped rules, evaluated once per worksheet. Where a rule names an anchorField the issue
// lands on that column; crossRowSum instead lands on whichever column each row used.
export type SheetRule =
  | { kind: "crossRowUnique"; fields: string[]; withinFields?: string[]; anchorField: string }
  | { kind: "exactlyOneOf"; fields: string[]; anchorField: string }
  | {
      kind: "crossRowSum";
      groupBy: string[];
      // What each column's values come to when they account for the whole — 100 for a
      // percentage, 1 for a fraction. A group may use several at once, so every share is
      // converted to its part of the whole before being added.
      expected: { totalByField: Record<string, number> };
      // A share of the total, not a number of units.
      tolerance: number;
    };

export type TemplateRule = {
  kind: "crossSheetSum";
  // Plain-language rule name for messages, e.g. "Trial balance".
  label: string;
  // Columns summed across every data row of each listed worksheet.
  terms: Array<{ worksheet: string; field: string }>;
  expected: number;
  tolerance: number;
};

// Rule overlay for one column, keyed by field name inside a WorksheetRuleConfig.
export type ColumnRuleConfig = {
  isNumeric?: boolean;
  rules: ValidationRule[];
};

export type WorksheetRuleConfig = {
  // Excel stores a cell shown as 25% as 0.25. Set where the figure on screen is the one meant,
  // so the share columns read 25. Left off elsewhere: scaling a cell changes what is submitted.
  readPercentCellsAsShown?: boolean;
  // Output CSV file name — unused by validation, needed when submission output is built.
  outputCsv: string;
  // Row the column names are on, for a worksheet with a header of several rows
  headerRow?: number;
  // Columns that give a row its identity. Absent = keep every row.
  keyFields?: string[];
  columns: Record<string, ColumnRuleConfig>;
  sheetRules?: SheetRule[];
};

export type TemplateRuleConfig = Record<string, WorksheetRuleConfig>;

// Zipped output of buildTemplateDefinition — generated field defs merged with the overlay.
export type ColumnDefinition = TemplateFieldDef & ColumnRuleConfig;

export type WorksheetDefinition = {
  worksheet: string;
  readPercentCellsAsShown: boolean;
  outputCsv: string;
  headerRow: number;
  keyFields: string[];
  columns: ColumnDefinition[];
  sheetRules: SheetRule[];
};

export type TemplateDefinition = {
  templateType: TemplateKind;
  worksheets: WorksheetDefinition[];
  templateRules: TemplateRule[];
};

export type WorksheetRelationship = {
  sourceWorksheet: string;
  // More than one when a value may match any of them.
  sourceFields: string[];
  targetWorksheet: string;
  targetField: string;
};
